/**
 * Utility functions for improved upload algorithm
 * Includes parallel processing, retry logic, and batch upload management
 */

/**
 * Process items in parallel with concurrency control
 */
export const processInBatches = async <T, R>(
  items: T[],
  processor: (item: T, index: number) => Promise<R>,
  concurrency: number = 3
): Promise<R[]> => {
  const results: R[] = [];
  const executing: Array<{ promise: Promise<void>; index: number }> = [];
  let currentIndex = 0;

  for (const item of items) {
    const index = currentIndex++;
    const promise = processor(item, index)
      .then(result => {
        results[index] = result;
      })
      .catch(error => {
        // Store error in results array
        results[index] = error as any;
      })
      .finally(() => {
        // Remove this promise from executing array when it completes
        const execIndex = executing.findIndex(e => e.index === index);
        if (execIndex !== -1) {
          executing.splice(execIndex, 1);
        }
      });

    executing.push({ promise, index });

    // Wait if we've reached concurrency limit
    while (executing.length >= concurrency) {
      await Promise.race(executing.map(e => e.promise));
      // The finally block above will have removed completed promises
      // But we need to check again in case multiple completed
    }
  }

  // Wait for all remaining promises to complete
  await Promise.all(executing.map(e => e.promise));
  return results;
};

/**
 * Retry a function with exponential backoff
 */
export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: Error;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      if (attempt < maxRetries - 1) {
        const delay = Math.pow(2, attempt) * baseDelay;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError!;
};

/**
 * Upload progress tracking interface
 */
export interface UploadProgress {
  fileId: string;
  fileName: string;
  status: 'pending' | 'uploading' | 'converting' | 'completed' | 'failed';
  progress: number; // 0-100
  error?: string;
}

/**
 * Upload error with context
 */
export interface UploadError {
  fileId: string;
  fileName: string;
  error: string;
  retryable: boolean;
  suggestion?: string;
}

/**
 * Analyze error and provide user-friendly feedback
 */
export const analyzeUploadError = (error: Error, fileName: string, fileId: string): UploadError => {
  const errorMessage = error.message.toLowerCase();

  if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
    return {
      fileId,
      fileName,
      error: 'Nätverksfel',
      retryable: true,
      suggestion: 'Kontrollera din internetanslutning och försök igen'
    };
  }

  if (errorMessage.includes('heif 2.0') || errorMessage.includes('auxiliary image')) {
    return {
      fileId,
      fileName,
      error: 'HEIF 2.0 stöds inte',
      retryable: false,
      suggestion: 'Konvertera bilden till JPEG eller PNG på din enhet innan uppladdning'
    };
  }

  if (errorMessage.includes('corrupt') || errorMessage.includes('invalid input')) {
    return {
      fileId,
      fileName,
      error: 'Ogiltig fil',
      retryable: false,
      suggestion: 'Filen kan vara skadad eller använda ett format som inte stöds. Försök konvertera till JPEG eller PNG.'
    };
  }

  if (errorMessage.includes('quota') || errorMessage.includes('storage')) {
    return {
      fileId,
      fileName,
      error: 'Lagringsutrymme fullt',
      retryable: false,
      suggestion: 'Kontakta administratören för att öka lagringsutrymmet'
    };
  }

  if (errorMessage.includes('permission') || errorMessage.includes('unauthorized')) {
    return {
      fileId,
      fileName,
      error: 'Behörighet saknas',
      retryable: false,
      suggestion: 'Du har inte behörighet att ladda upp filer. Kontakta administratören.'
    };
  }

  return {
    fileId,
    fileName,
    error: error.message || 'Okänt fel',
    retryable: true,
    suggestion: 'Försök igen om en stund'
  };
};

/**
 * Compress image before upload (optional)
 */
export const compressImage = async (
  file: File,
  maxWidth: number = 1920,
  quality: number = 0.85
): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Skip compression for HEIC files - they'll be converted on server
    if (file.type === 'image/heic' || file.type === 'image/heif' || 
        file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
      resolve(file);
      return;
    }

    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(file); // Fallback to original
          return;
        }

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (blob && blob.size < file.size) {
              // Only use compressed version if it's smaller
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            } else {
              resolve(file); // Fallback to original
            }
          },
          'image/jpeg',
          quality
        );
      } catch (error) {
        resolve(file); // Fallback to original on error
      }
    };

    img.onerror = () => {
      resolve(file); // Fallback to original on error
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Create a simple thumbnail for HEIC files (placeholder)
 * In the future, this could use heic2any or similar library
 */
export const createHeicThumbnail = async (file: File): Promise<string> => {
  // For now, return empty string - we'll show a placeholder in the UI
  // In the future, could use heic2any library:
  // import heic2any from 'heic2any';
  // const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.5 });
  // return URL.createObjectURL(convertedBlob);
  return '';
};

/**
 * Batch upload with progress tracking
 */
export const batchUpload = async <T>(
  items: T[],
  uploadFn: (item: T, onProgress: (progress: number) => void) => Promise<void>,
  batchSize: number = 4,
  onItemProgress?: (itemIndex: number, progress: number) => void
): Promise<void> => {
  const batches: T[][] = [];
  
  for (let i = 0; i < items.length; i += batchSize) {
    batches.push(items.slice(i, i + batchSize));
  }

  for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
    const batch = batches[batchIndex];
    
    await Promise.allSettled(
      batch.map(async (item, itemIndexInBatch) => {
        const globalIndex = batchIndex * batchSize + itemIndexInBatch;
        
        try {
          await uploadFn(item, (progress) => {
            if (onItemProgress) {
              onItemProgress(globalIndex, progress);
            }
          });
        } catch (error) {
          // Error handling is done in uploadFn
          throw error;
        }
      })
    );
  }
};

